import { observer } from 'mobx-react';
import { useTranslation } from 'react-i18next';

import { CoinWithValue } from '../../../../../components/CoinWithValue/CoinWithValue';
import { useStores } from '../../../../../stores/useStores';
import { WidgetCrownBadge, WidgetCrownJpLabel } from '../JackpotWidgetArt';

import styles from './JackpotSmallWidget.module.scss';

interface JackpotSmallWidgetProps {
  /**
   * Render the maintenance plate instead of the pot — the `Disabled` variant of `Widget - Small`
   * (`10929:24559`). The badge and ribbon are unchanged; only the bottom strip differs, because in a
   * maintenance window there is no amount to show: the flag is off, so nothing was ever fetched.
   */
  maintenance?: boolean;
}

/**
 * FE_DESIGN_SPEC §7.4 variant C — the round nav-rail icon: circular crown badge, `CROWN JP` ribbon,
 * coin + the top level pot beneath (the biggest prize on the ladder, by association priority).
 *
 * Component `10083:17690` (`Widget - Small` / `Default`), 60 x 74. The three boxes are a flow stack
 * with the frame's own 2px overlap between them — see the module's `.container` note for why they are
 * no longer pinned to the bottom of the rail slot.
 *
 * Display only; the wrapper owns the tap that opens the jackpot popup, and there is deliberately no
 * opt-in control here.
 */
function JackpotSmallWidgetComponent({ maintenance = false }: JackpotSmallWidgetProps) {
  const { t } = useTranslation();
  const { coinTypeStore, jackpotStore } = useStores();

  const { topLevelAmountLabel } = jackpotStore;

  return (
    <div className={styles.container}>
      {/* The ribbon sits over the badge's lower edge, not under it — frame `17654:64942` puts the
          badge at y0-58 and the label at y42-56. */}
      <div className={styles.badge}>
        <WidgetCrownBadge className={styles.badgeArt} />
        <WidgetCrownJpLabel alt={t('jackpot.navLabel')} className={styles.label} />
      </div>
      {maintenance ? (
        /* 60x18 plate, flat purple, bottom corners only — the pot's coin row is not drawn at all. */
        <div className={styles.maintenance} data-testid="jackpot-widget-maintenance-label">
          {t('jackpot.maintenance')}
        </div>
      ) : (
        <div className={styles.content}>
          {/* Coin icon per coin type: the gold CC coin or the green SC coin, same as the category row. */}
          <CoinWithValue
            className={styles.amount}
            coinSide="left"
            coinType={coinTypeStore.coinType}
            // 8 x 8, per the coin instance `10083:17680`.
            coinWidth="8px"
            dataTestId="jackpot-widget-amount"
            valueClassName={styles.amountValue}
          >
            {/*
              The rounded figure ("487.2M"), matching the category row and the promo card — the
              store's label owns the rule (formatJackpotAmountRounded). The 9px type survives from
              the full-figure era, when text `10083:17596` fit "487,154,146" into a 47px box.
            */}
            {topLevelAmountLabel}
          </CoinWithValue>
        </div>
      )}
    </div>
  );
}

export const JackpotSmallWidget = observer(JackpotSmallWidgetComponent);
